// Candle Lighting Metrics Tracking Utility
import config from '../config';

class CandleLightingMetrics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.shareCode = null;
    this.pageLoadTime = null;
    this.pageStartTime = Date.now();
    this.urlParams = new URLSearchParams(window.location.search);
  }

  generateSessionId() {
    // Check if session ID exists in sessionStorage
    let sessionId = sessionStorage.getItem('candle_lighting_session_id');
    if (!sessionId) {
      sessionId = this.generateUUID();
      sessionStorage.setItem('candle_lighting_session_id', sessionId);
    }
    return sessionId;
  }

  generateVisitorId() {
    // Check if visitor ID exists in localStorage (persists across sessions)
    let visitorId = localStorage.getItem('candle_lighting_visitor_id');
    if (!visitorId) {
      visitorId = this.generateUUID();
      localStorage.setItem('candle_lighting_visitor_id', visitorId);
    }
    return visitorId;
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getURLSource() {
    // Check URL parameters for source
    const source = this.urlParams.get('source') || 
                   this.urlParams.get('utm_source') || 
                   this.urlParams.get('ref');
    
    if (source) return source;
    
    // Check referrer
    const referrer = document.referrer;
    if (!referrer) return 'direct';
    
    if (referrer.includes('whatsapp')) return 'whatsapp';
    if (referrer.includes('facebook')) return 'facebook';
    if (referrer.includes('instagram')) return 'instagram';
    if (referrer.includes('twitter') || referrer.includes('t.co')) return 'twitter';
    if (referrer.includes('google')) return 'google';
    
    return 'referral';
  }

  getCityParam() {
    const path = window.location.pathname;
    const match = path.match(/candle-lighting\/[^/]+\/([^/]+)/);
    return match ? match[1] : null;
  }

  async logEvent(eventType, eventAction, options = {}) {
    try {
      const payload = {
        session_id: this.sessionId,
        visitor_id: this.generateVisitorId(),
        share_code: this.shareCode,
        event_type: eventType,
        event_action: eventAction,
        event_label: options.label || null,
        event_value: options.value || null,
        page_url: window.location.href,
        url_source: this.getURLSource(),
        city_param: this.getCityParam(),
        referrer: document.referrer || null,
        utm_source: this.urlParams.get('utm_source') || null,
        utm_medium: this.urlParams.get('utm_medium') || null,
        utm_campaign: this.urlParams.get('utm_campaign') || null,
        page_load_time: this.pageLoadTime,
        time_on_page: Math.floor((Date.now() - this.pageStartTime) / 1000),
        metadata: options.metadata || null
      };

      const response = await fetch(`${config.API_URL}/candle-metrics/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.error('Failed to log metric:', response.statusText);
      }
    } catch (error) {
      console.error('Error logging metric:', error);
    }
  }

  // Convenience methods for common events
  logPageView(shareCode) {
    this.shareCode = shareCode;
    this.logEvent('page_view', 'initial_load');
  }

  logButtonClick(buttonName, metadata = {}) {
    this.logEvent('button_click', buttonName, { metadata });
  }

  logModalOpen(modalName) {
    this.logEvent('modal_open', modalName);
  }

  logModalClose(modalName) {
    this.logEvent('modal_close', modalName);
  }

  logMusicPlay() {
    this.logEvent('music_play', 'audio_started');
  }

  logMusicStop(reason = 'audio_paused') {
    this.logEvent('music_stop', reason);
  }

  logShareAction(platform) {
    this.logEvent('share_action', platform);
  }

  logReminderSubmitted(method, metadata = {}) {
    this.logEvent('reminder_submitted', method, { metadata });
  }

  logCountdownView() {
    this.logEvent('countdown_view', 'countdown_visible');
  }

  logImageCarousel(action, index) {
    this.logEvent('image_carousel', action, { 
      metadata: { image_index: index } 
    });
  }

  setPageLoadTime(time) {
    this.pageLoadTime = time;
  }
}

// Export singleton instance
const candleLightingMetrics = new CandleLightingMetrics();
export default candleLightingMetrics;
